import type { QualifyingStatusProduct } from '@iheartjane/data-access';
import { useBrandSpecialQualifyingStatus } from '@iheartjane/data-access/react-query';
import { buildFilter } from '@iheartjane/menu';
import find from 'lodash-es/find';
import { useEffect } from 'react';

import { AVAILABLE_WEIGHTS } from '@jane/search/util';
/* eslint-disable-next-line no-restricted-imports */
import type { BrandSpecial as tBrandSpecial } from '@jane/shared/models';
import type { BrandSpecial, Store } from '@jane/shared/types';
import { getApplicablePriceIds } from '@jane/shared/util';

import {
  FilterAndSort,
  JaneGoldCallout,
  MenuProducts,
  SpecialDetail,
  getColumnCount,
  useSpecialDetailContext,
} from '../../components';
import {
  useGetCart,
  useRemoveCartProduct,
  useUpdateCartProduct,
} from '../../data-access';
import { useMenuProductSearch, useProductTracking } from '../../providers';
import { getBrandSpecialStatusText } from '../../util';

interface BrandSpecialInnerProps {
  brandSpecial: BrandSpecial | tBrandSpecial;
  navigateToCart: () => void;
  navigateToMenu: () => void;
  store: Store;
}

export const BrandSpecialInner = ({
  brandSpecial,
  navigateToCart,
  navigateToMenu,
  store,
}: BrandSpecialInnerProps) => {
  const defaultSortName = store?.optional_filters ? 'Featured' : null;

  const { data: cart } = useGetCart();
  const { data: qualifyingStatusData, refetch: fetchStatus } =
    useBrandSpecialQualifyingStatus({
      cartUuid: cart?.uuid,
      specialId: brandSpecial?.id,
      storeId: store?.id,
    });

  const qualifyingStatus = qualifyingStatusData?.qualifying_status;
  const { calloutText, reachedMaxDiscountables, remainingQualifiers } =
    getBrandSpecialStatusText({
      currentSpecial: brandSpecial,
      qualifyingStatus: qualifyingStatusData,
    });

  const applicablePriceIds = getApplicablePriceIds(
    brandSpecial,
    qualifyingStatus ?? 'not_qualified'
  );

  // if there are no weight restrictions, we return all of the weights
  const visiblePriceIds =
    applicablePriceIds.length !== AVAILABLE_WEIGHTS.length
      ? applicablePriceIds
      : null;

  // if only some weights are returned, we want to filter by weight
  const availableWeightsFilter = visiblePriceIds
    ? buildFilter(
        'available_weights',
        visiblePriceIds.map((priceId) => priceId.split('_').join(' '))
      )
    : '';

  const productsQuery = useMenuProductSearch({
    brandSpecial: brandSpecial,
    category: 'all',
    columnCount: getColumnCount(),
    defaultSortName,
    featuredCategory: availableWeightsFilter,
    isEnabled: !!brandSpecial,
    qualifyingStatus: qualifyingStatusData?.qualifying_status,
    store,
  });

  // sets the correct top value so the sticky headers group together
  const pageHeaderHeight =
    // eslint-disable-next-line @iheartjane/workspace/no-dom-globals-in-react-fc
    window.document.getElementById('brand-special-page-header')?.clientHeight ||
    0;

  const discountedWithoutSameQualified =
    qualifyingStatusData?.discounted_products
      ? qualifyingStatusData.discounted_products.filter(
          (product) =>
            !find(qualifyingStatusData.qualified_products, {
              id: product.id,
              price_id: product.price_id,
            })
        )
      : [];

  const discountedProductsToShow =
    brandSpecial?.special_type === 'buy_x_and_save'
      ? discountedWithoutSameQualified
      : qualifyingStatusData?.discounted_products;

  const { mutate: removeCartProduct } = useRemoveCartProduct({});
  const { mutate: updateProduct } = useUpdateCartProduct({});
  const {
    cartToppers: { removeCartTopperId },
    inlineAds: { removeInlineAdId },
  } = useProductTracking();

  const handleRemoveProduct = (product: QualifyingStatusProduct) => {
    const cartProduct = cart?.products.find(
      ({ id, price_id }) => id === product.id && price_id === product.price_id
    );

    if (!cartProduct) return;

    if (cartProduct.count === 1) {
      removeCartProduct(
        {
          cart_uuid: cart?.uuid ?? null,
          product_id: cartProduct.id,
        },
        {
          onSuccess: () => {
            removeInlineAdId(cartProduct.id);
            removeCartTopperId(cartProduct.id);
          },
        }
      );
    } else {
      updateProduct({
        cart_uuid: String(cart?.uuid),
        product: {
          count: cartProduct.count - 1,
          price_id: cartProduct.price_id,
          product_id: cartProduct.product_id,
        },
      });
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [cart?.uuid, cart?.products, fetchStatus]);

  if (!brandSpecial) return null;

  return (
    <SpecialDetail>
      <SpecialDetail.Layout top={pageHeaderHeight}>
        <JaneGoldCalloutContainer storeState={store.state} />
        <SpecialDetail.Header
          brandImage={brandSpecial.product_brand?.logo_url ?? undefined}
          brandName={brandSpecial.product_brand?.name ?? ''}
          specialDescription={brandSpecial.description}
          specialTitle={brandSpecial.title}
          specialTerms={brandSpecial.terms ?? undefined}
        >
          {brandSpecial?.special_type !== 'product' && (
            <SpecialDetail.Progress
              allowQualifiersToBeDiscounted={
                brandSpecial?.special_type === 'buy_x_and_save'
              }
              discountedProducts={
                (discountedProductsToShow as QualifyingStatusProduct[]) || []
              }
              isGold
              onRemoveProduct={(product) => handleRemoveProduct(product)}
              qualifiedProducts={
                (qualifyingStatusData?.qualified_products as QualifyingStatusProduct[]) ||
                []
              }
              qualifyingStatus={qualifyingStatus || 'not_qualified'}
              reachedMaxDiscountables={reachedMaxDiscountables}
              remainingQualifiers={remainingQualifiers}
              tagLabel={brandSpecial?.discount_label || ''}
            />
          )}
        </SpecialDetail.Header>

        {brandSpecial?.special_type !== 'product' && (
          <SpecialDetail.Callout calloutText={calloutText} isGold />
        )}
        {brandSpecial?.special_type !== 'product' && (
          <SpecialDetail.Footer
            onCartClick={navigateToCart}
            onShopMoreClick={navigateToMenu}
            qualifyingStatus={qualifyingStatus || 'not_qualified'}
          />
        )}
      </SpecialDetail.Layout>

      <FilterAndSort
        condensedView
        defaultSortName={defaultSortName}
        optionalFilters={store.optional_filters}
        searchProps={productsQuery}
      />
      <MenuProducts
        category="jane-gold"
        searchProps={productsQuery}
        showProductCount={false}
        visiblePriceIds={visiblePriceIds ?? undefined}
      />
    </SpecialDetail>
  );
};

const JaneGoldCalloutContainer = ({
  storeState,
}: {
  storeState?: string | null;
}) => {
  const { scrolledFromTop } = useSpecialDetailContext();
  return (
    <JaneGoldCallout
      variant="user"
      scrolledFromTop={scrolledFromTop}
      storeState={storeState}
    />
  );
};
